import { Alert, Button, Field, Input, Spinner } from '@personachat/ui';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { lire, messageDe } from '../api/call';
import { publicApi } from '../api/publicClient';

/** Règles du mot de passe d'un compte admin (vérifiées aussi par l'API). */
export function reglesMotDePasse(mdp: string): Array<{ libelle: string; ok: boolean }> {
  return [
    { libelle: '12 caractères au moins', ok: mdp.length >= 12 },
    { libelle: 'une minuscule', ok: /[a-z]/.test(mdp) },
    { libelle: 'une majuscule', ok: /[A-Z]/.test(mdp) },
    { libelle: 'un chiffre', ok: /\d/.test(mdp) },
  ];
}

/**
 * Invitation d'un admin (cahier des charges §7.11) : le lien reçu par e-mail
 * ouvre cette page, qui fait choisir le mot de passe du compte. Page publique.
 */
export function InvitationPage() {
  const [params] = useSearchParams();
  const jeton = params.get('token') ?? '';
  const [mdp, setMdp] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [fait, setFait] = useState(false);

  const invitation = useQuery({
    queryKey: ['invitation', jeton],
    queryFn: () => lire(publicApi.GET('/api/auth/invitation', { params: { query: { token: jeton } } })),
    enabled: !!jeton,
    retry: false,
  });

  const regles = reglesMotDePasse(mdp);
  const valide = regles.every((r) => r.ok) && mdp === confirmation;

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    if (!valide || envoi) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await lire(publicApi.POST('/api/auth/invitation/accept', { body: { token: jeton, password: mdp } }));
      setFait(true);
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="login">
      <div className="login__card">
        <div className="login__brand">
          <span className="sidebar__logo" aria-hidden="true">
            M
          </span>
          <div>
            <h1>Back-office</h1>
            <p>Invitation · MyBizGame IA</p>
          </div>
        </div>

        {!jeton ? (
          <Alert>Lien incomplet : ouvrez le lien reçu par e-mail en entier.</Alert>
        ) : invitation.isLoading ? (
          <Spinner label="Vérification du lien" />
        ) : invitation.isError ? (
          <>
            <Alert>{messageDe(invitation.error)}</Alert>
            <Link className="lien-discret" to="/connexion">
              Aller à la connexion
            </Link>
          </>
        ) : fait ? (
          <>
            <Alert tone="info">Compte activé. Connectez-vous avec {invitation.data?.email} et ce mot de passe.</Alert>
            <Link className="ui-btn ui-btn--primary ui-btn--block lien-bouton" to="/connexion">
              Se connecter
            </Link>
          </>
        ) : (
          <form className="stack" onSubmit={soumettre} noValidate>
            <p className="texte-aide">
              Choisissez le mot de passe du compte <strong>{invitation.data?.email}</strong>.
            </p>
            {erreur && <Alert>{erreur}</Alert>}
            <Field id="mdp" label="Mot de passe">
              <Input id="mdp" type="password" autoComplete="new-password" value={mdp} onChange={(e) => setMdp(e.target.value)} autoFocus />
            </Field>
            <ul className="regles" aria-label="Règles du mot de passe">
              {regles.map((r) => (
                <li key={r.libelle} className={r.ok ? 'regles__ok' : undefined}>
                  {r.ok ? '✓' : '·'} {r.libelle}
                </li>
              ))}
            </ul>
            <Field
              id="confirmation"
              label="Confirmation"
              error={confirmation && confirmation !== mdp ? 'Les deux mots de passe diffèrent.' : null}
            >
              <Input
                id="confirmation"
                type="password"
                autoComplete="new-password"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
              />
            </Field>
            <Button type="submit" variant="primary" block loading={envoi} disabled={!valide}>
              Activer mon compte
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
